import React from 'react';
import { Modal, Descriptions, Typography, Spin, Tag } from 'antd';
import { FileProtectOutlined, CheckCircleOutlined, CloseCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import VolunteerRegistrationService from '../../../services/VolunteerRegistrationService';
import {
  ActionButton,
  SignaturePreview,
  AcknowledgmentList,
  AcknowledgmentItem,
  WaiverEventCard
} from '../styled';

const { Title, Text } = Typography;

const renderAcknowledgments = (registration) => {
  const acknowledgments = [
    { key: 'acknowledgedRisks', label: 'Acknowledged Risks' },
    { key: 'acknowledgedLiability', label: 'Acknowledged Liability' },
    { key: 'acknowledgedSafetyInstructions', label: 'Acknowledged Safety Instructions' },
    { key: 'acknowledgedEmergencyAuth', label: 'Authorized Emergency Care' },
    { key: 'acknowledgedMedicalExpenses', label: 'Acknowledged Medical Expenses' }
  ];

  // If the single acknowledgedWaiver field is true, treat all as acknowledged
  const waiverCoverAll = registration.acknowledgedWaiver === true;

  return (
    <AcknowledgmentList>
      {acknowledgments.map(ack => {
        const isAcknowledged = registration[ack.key] || waiverCoverAll;
        return (
          <AcknowledgmentItem key={ack.key} $acknowledged={isAcknowledged}>
            {isAcknowledged ? (
              <CheckCircleOutlined className="icon" />
            ) : (
              <CloseCircleOutlined className="icon" />
            )}
            <span>{ack.label}</span>
          </AcknowledgmentItem>
        );
      })}
    </AcknowledgmentList>
  );
};

const WaiverModal = ({
  waiverModal,
  onClose
}) => {
  const handleDownloadWaiverPdf = (registration) => {
    if (!registration._id || !registration.downloadToken) {
      return;
    }
    
    const url = VolunteerRegistrationService.getPdfDownloadUrl(registration._id, registration.downloadToken);
    window.open(url, '_blank');
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FileProtectOutlined style={{ color: '#52c41a' }} />
          <span>Waiver & Consent - {waiverModal.volunteerName}</span>
        </div>
      }
      open={waiverModal.visible}
      onCancel={onClose}
      width={700}
      footer={[
        <ActionButton key="close" onClick={onClose}>
          Close
        </ActionButton>
      ]}
    >
      {waiverModal.loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px' }}>Loading waiver information...</p>
        </div>
      ) : waiverModal.registrations.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Text type="secondary">No waiver/consent forms found for this volunteer.</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            The volunteer may not have completed the registration form yet.
          </Text>
        </div>
      ) : (
        <div>
          <Text type="secondary" style={{ marginBottom: '16px', display: 'block' }}>
            Found {waiverModal.registrations.length} waiver form(s) for {waiverModal.volunteerEmail}
          </Text>
          
          {waiverModal.registrations.map((registration, index) => (
            <WaiverEventCard key={registration._id} $isLatest={index === 0}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <Title level={5} style={{ margin: 0 }}>
                    {registration.preferredCity ? `${registration.preferredCity}, ${registration.preferredState || ''}`.trim() : 'Any Location'}
                    {index === 0 && <Tag color="green" style={{ marginLeft: '8px' }}>Latest</Tag>}
                  </Title>
                  <Text type="secondary">
                    {new Date(registration.createdAt).toLocaleDateString()}
                  </Text>
                </div>
                <ActionButton
                  type="primary"
                  ghost
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownloadWaiverPdf(registration)}
                >
                  Download PDF
                </ActionButton>
              </div>
              
              <Descriptions size="small" column={2}>
                <Descriptions.Item label="Volunteer Area">
                  <Tag color="blue">{registration.volunteerArea}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Medical Experience">
                  <Tag color="purple">{registration.medicalExperience}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Waiver Version">
                  {registration.waiverVersion || '1.0'}
                </Descriptions.Item>
                <Descriptions.Item label="Signed At">
                  {registration.waiverSignedAt 
                    ? new Date(registration.waiverSignedAt).toLocaleString()
                    : '-'
                  }
                </Descriptions.Item>
                <Descriptions.Item label="Printed Name" span={2}>
                  {registration.printedName}
                </Descriptions.Item>
              </Descriptions>
              
              <Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>
                Acknowledgments
              </Title>
              {renderAcknowledgments(registration)}
              
              <Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>
                Signature
              </Title>
              <SignaturePreview>
                {registration.signatureType === 'draw' && registration.signatureData ? (
                  <img 
                    src={registration.signatureData} 
                    alt="Signature" 
                  />
                ) : registration.typedSignature ? (
                  <div className="typed-signature">
                    {registration.typedSignature}
                  </div>
                ) : (
                  <Text type="secondary">No signature available</Text>
                )}
              </SignaturePreview>
            </WaiverEventCard>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default WaiverModal;
